import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SecurityAccountView } from '@ni/shared';
import { useApi, useOrg } from '../../api/app-context';
import { loadErrorLine } from '../../api/errors';
import { orgHasProtectionAccounts } from '../../lib/vertical';
import { Pick } from '../ui/pick';
import './account-choice.css';

/**
 * Fetch every bounded page so an account beyond page one remains selectable.
 *
 * Off in a vertical that has no accounts: the route would refuse the read, and
 * the pickers this feeds are not rendered there anyway.
 */
export function useSecurityAccounts(state: 'active' | 'all' = 'all') {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: ['security', org.id, 'account-options', state],
    enabled: orgHasProtectionAccounts(org),
    queryFn: async () => {
      const accounts: SecurityAccountView[] = [];
      for (let offset = 0; ; offset += 50) {
        const page = await api.securityAccounts(state, offset);
        accounts.push(...page.accounts);
        if (offset + page.limit >= page.total || !page.accounts.length) return accounts;
      }
    },
  });
}

/**
 * The account chip: one select, shared by every surface that narrows on an
 * account — the People list, the Organizations list, the knowledge base, and
 * the two create doors, which pass `active` because an archived engagement
 * takes no new records.
 *
 * `pick` sets it as one more quiet select in a list's toolbar (the People
 * list's), its label inside it; the default is a labeled field.
 */
export function AccountChoice({
  value,
  onChange,
  active = false,
  empty = 'Choose a protection account',
  variant = 'field',
}: {
  value: string;
  onChange: (id: string) => void;
  active?: boolean;
  empty?: string;
  variant?: 'field' | 'pick';
}) {
  const accounts = useSecurityAccounts(active ? 'active' : 'all');
  const pickId = useId();
  const options = (
    <>
      <option value="">{empty}</option>
      {accounts.data?.map((a) => (
        <option key={a.id} value={a.id}>
          {a.label}
          {a.state === 'archived' ? ' · Archived' : ''}
        </option>
      ))}
    </>
  );
  return (
    <>
      {variant === 'pick' ? (
        <Pick label="Account" htmlFor={pickId} className="account-pick">
          <select id={pickId} value={value} onChange={(e) => onChange(e.target.value)}>
            {options}
          </select>
        </Pick>
      ) : (
        <label className="field account-filter">
          <span className="label">Protection account</span>
          <select className="control" value={value} onChange={(e) => onChange(e.target.value)}>
            {options}
          </select>
        </label>
      )}
      {accounts.error ? <p role="alert">{loadErrorLine(accounts.error)}</p> : null}
    </>
  );
}
